import type LogRocketType from 'logrocket';

import * as datadogRum from 'lib/telemetry/datadogRum';
import type { AccessLevel } from 'models/user';
import config from 'utils/config';
import { isConstrainedDevice } from 'utils/device';
import { sanitizeKeysThatContainsAnyString, sanitizeURLThatContainsAnyString } from 'utils/objects';

type LogrocketEvent = 'first_deposit' | 'geocomply_eligible' | 'is_employee';
type LogRocketSdk = typeof LogRocketType;
type LogRocketBootPolicy = 'standard' | 'constrained_deferred' | 'employee_standard';

/**
 * LogRocket session-aware client.
 *
 * App lifecycle: the SDK is dynamic-`import()`ed once per page load after the
 * first `startSession` (driven off auth, not first paint). Loading waits until
 * the window load event and then browser idle, so LogRocket does not compete
 * with hydration. It boots in buffering mode — session data is captured locally
 * but NOT uploaded until LogRocket's conditional recording rules are satisfied.
 * The `first_deposit` event is configured in the dashboard to trigger
 * targeted/sampled recording around the deposit attempt.
 *
 * @see https://docs.logrocket.com/reference/conditional-recording
 *
 * Session lifecycle: `startSession` identifies the user and rotates
 * LogRocket's session whenever the user changes. Session bookkeeping
 * (`lastUserId` / `sessionActive` / `trackedEvents`) is synchronous; only the
 * `LogRocket.*` calls are deferred and buffered until the SDK loads.
 */

let sdk: LogRocketSdk | null = null;
let loadPromise: Promise<LogRocketSdk | null> | null = null;
let loadScheduled = false;
// Handle of an outstanding `requestIdleCallback`, plus whether it was requested
// without a timeout. A timeoutless request can starve indefinitely on a busy main
// thread, so it has to be cancelled and re-requested if the boot policy relaxes
// before it fires (see `scheduleLoad`).
let pendingIdleHandle: number | null = null;
let pendingIdleIsTimeoutless = false;
let bootPolicy: LogRocketBootPolicy = 'standard';
const queue: Array<(lr: LogRocketSdk) => void> = [];
const MAX_QUEUE = 100;
const IDLE_LOAD_TIMEOUT_MS = 5000;

// Persists across `endSession` so the next `startSession` can detect when a
// different user logs in and rotate LogRocket for that user switch.
let lastUserId: string | null = null;
let sessionActive = false;
// GeoComply eligibility is evaluated in a sibling React effect that can commit
// before the auth-driven LogRocket session effect. Hold that signal until the
// matching user session starts so it is neither dropped nor attributed to the
// previously active user.
let pendingGeoComplyEligibleUserId: string | null = null;
// Bumped on every `endSession`. A queued `identify` captures the epoch it was
// enqueued in; if the SDK load is deferred past a logout, that lets us tell an
// uninterrupted user switch (same epoch — still valid) from an op orphaned by a
// logout (older epoch — only valid if the same user has since logged back in).
let sessionEpoch = 0;
const trackedEvents = new Set<LogrocketEvent>();

function doInit(LogRocket: LogRocketSdk, logRocketId: string): void {
    // Read lazily (not at module scope) so importing this module stays free of
    // config side effects — the code path only runs once LogRocket boots.
    const sanitizeWordsWithoutCode = config.logRocketSanitizeWords.filter((word) => word !== 'code');
    // Keep generated API credentials protected even when a deployment overrides the configured words.
    const protectedResponseFields = [...sanitizeWordsWithoutCode, 'api_key_id', 'private_key'];
    LogRocket.init(logRocketId, {
        network: {
            requestSanitizer: (request) => {
                if (request.body) {
                    try {
                        request.headers = sanitizeKeysThatContainsAnyString(
                            request.headers,
                            config.logRocketSanitizeWords,
                        );
                        const jsonBody = JSON.parse(request.body);
                        const filteredJsonBody = sanitizeKeysThatContainsAnyString(
                            jsonBody,
                            config.logRocketSanitizeWords,
                        );
                        request.body = JSON.stringify(filteredJsonBody);
                    } catch {
                        // Not a JSON request — nothing to sanitize
                    }
                }

                if (request.url) {
                    request.url = sanitizeURLThatContainsAnyString(request.url, config.logRocketSanitizeWords);
                }

                return request;
            },
            responseSanitizer: (response) => {
                if (response.body) {
                    try {
                        response.headers = sanitizeKeysThatContainsAnyString(
                            response.headers,
                            config.logRocketSanitizeWords,
                        );
                        const jsonBody = JSON.parse(response.body);
                        const filteredJsonBody = sanitizeKeysThatContainsAnyString(jsonBody, protectedResponseFields);
                        response.body = JSON.stringify(filteredJsonBody);
                    } catch {
                        // Not a JSON response — nothing to sanitize
                    }
                }

                return response;
            },
        },
        browser: {
            urlSanitizer: (url): string => sanitizeURLThatContainsAnyString(url, config.logRocketSanitizeWords),
        },
    });
}

function load(): void {
    if (loadPromise || typeof window === 'undefined' || !config.logRocketId) return;

    loadScheduled = false;
    pendingIdleHandle = null;
    pendingIdleIsTimeoutless = false;

    const logRocketId = config.logRocketId;
    loadPromise = import('logrocket')
        .then((m) => {
            const LogRocket = m.default;
            doInit(LogRocket, logRocketId);
            sdk = LogRocket;
            for (const op of queue.splice(0)) op(LogRocket);
            return LogRocket;
        })
        .catch((err: unknown) => {
            queue.length = 0;
            console.warn('LogRocket failed to load', err);
            return null;
        });
}

function scheduleLoad(): void {
    if (loadPromise || typeof window === 'undefined' || !config.logRocketId) return;

    if (loadScheduled) {
        // A timeoutless idle request was made for a constrained member and has not
        // fired yet. If the policy has since relaxed — e.g. that member logs out and
        // an employee logs in — the outstanding request would leave the new session
        // deferred indefinitely on a busy main thread, so drop it and re-request with
        // the timeout. Anything else already pending honors the live policy when it
        // runs and is left alone.
        if (!pendingIdleIsTimeoutless || bootPolicy === 'constrained_deferred') return;

        if (pendingIdleHandle !== null && typeof window.cancelIdleCallback === 'function') {
            window.cancelIdleCallback(pendingIdleHandle);
        }
        // Without `cancelIdleCallback` the stale request survives and may still call
        // `load`, which is idempotent — it no-ops once `loadPromise` is set.
        pendingIdleHandle = null;
        pendingIdleIsTimeoutless = false;
        loadScheduled = false;
    }

    loadScheduled = true;

    const loadWhenIdle = () => {
        if (typeof window.requestIdleCallback === 'function') {
            // A timeout forces the callback onto a busy main thread. That is useful for
            // normal sessions, but counterproductive on the low-memory / slow-network
            // mobile cohort where LogRocket was itself a frequent long-task source.
            if (bootPolicy === 'constrained_deferred') {
                pendingIdleIsTimeoutless = true;
                pendingIdleHandle = window.requestIdleCallback(load);
            } else {
                pendingIdleIsTimeoutless = false;
                pendingIdleHandle = window.requestIdleCallback(load, { timeout: IDLE_LOAD_TIMEOUT_MS });
            }
            return;
        }

        // Browsers that expose the constrained-device signals also normally expose
        // requestIdleCallback. If one does not, skip background boot instead of forcing
        // LogRocket onto its main thread; a first-deposit trigger can still boot it.
        if (bootPolicy === 'constrained_deferred') {
            loadScheduled = false;
            return;
        }

        window.setTimeout(load, 0);
    };

    if (document.readyState === 'complete') {
        loadWhenIdle();
        return;
    }

    window.addEventListener('load', loadWhenIdle, { once: true });
}

/**
 * Run `op` against the SDK, loading + initializing it lazily on first use.
 *
 * Pass `immediate` to bypass the idle deferral and boot the SDK right away. This
 * is required for conditional-recording triggers: LogRocket only buffers session
 * data once initialized, so deferring the load past the trigger would leave the
 * event's funnel uncaptured.
 */
function withSdk(op: (lr: LogRocketSdk) => void, immediate = false): void {
    if (typeof window === 'undefined' || !config.logRocketId) return;
    if (sdk) {
        op(sdk);
        return;
    }
    if (queue.length >= MAX_QUEUE) queue.shift();
    queue.push(op);
    if (immediate) {
        load();
    } else {
        scheduleLoad();
    }
}

/**
 * A queued session op (`identify`/`track`) may not drain until the SDK finally
 * loads — potentially after the user logged out or switched accounts. It should
 * fire only while a session is active AND it wasn't orphaned by a logout to a
 * *different* user: an uninterrupted switch keeps the same epoch, and a same-user
 * re-login re-matches `lastUserId`. `epoch`/`userId` are captured when the op is
 * enqueued and compared against the live state here at drain time.
 */
function sessionOpStillValid(epoch: number, userId: string | null): boolean {
    if (!sessionActive) return false;
    if (epoch !== sessionEpoch && userId !== lastUserId) return false;
    return true;
}

export function startSession(input: { userId: string; accessLevel: AccessLevel }): void {
    if (typeof window === 'undefined' || !config.logRocketId) return;

    const isEmployee = input.accessLevel === 'internal_member' || input.accessLevel === 'admin';
    bootPolicy = isEmployee ? 'employee_standard' : isConstrainedDevice() ? 'constrained_deferred' : 'standard';
    datadogRum.setGlobalContextProperty('logrocket_boot_policy', bootPolicy);

    const userChanged = !!lastUserId && lastUserId !== input.userId;
    const userId = input.userId;
    const epoch = sessionEpoch;
    const hasPendingGeoComplyEligibility = pendingGeoComplyEligibleUserId === userId;
    pendingGeoComplyEligibleUserId = null;
    lastUserId = userId;
    sessionActive = true;
    trackedEvents.clear();

    withSdk((LogRocket) => {
        if (!sessionOpStillValid(epoch, userId)) return;
        if (userChanged) {
            LogRocket.startNewSession();
        }
        LogRocket.identify(userId);
    });

    if (isEmployee) {
        trackOnce('is_employee');
    }
    if (hasPendingGeoComplyEligibility) {
        trackOnce('geocomply_eligible');
    }
}

export function endSession(): void {
    sessionActive = false;
    trackedEvents.clear();
    datadogRum.setGlobalContextProperty('logrocket_boot_policy', 'signed_out');
    // Advance the epoch so any `identify` still queued behind a deferred load is
    // recognized as orphaned by this logout when it eventually drains.
    sessionEpoch += 1;
}

export function trackFirstDeposit(): void {
    trackOnce('first_deposit');
}

export function trackGeoComplyEligible(userId: string): void {
    if (typeof window === 'undefined' || !config.logRocketId) return;

    if (!sessionActive || lastUserId !== userId) {
        pendingGeoComplyEligibleUserId = userId;
        return;
    }

    trackOnce('geocomply_eligible');
}

/**
 * Which takeover the sports-location boundary put in place of a protected
 * trade action. `checking` is deliberately absent: it is a sub-second wait on
 * the way to one of these, and recording it would swamp the signal.
 */
export type GeoComplyBoundaryOutcome = 'verify_prompt' | 'blocked_region' | 'blocked_software' | 'service_unavailable';

/**
 * Emitted every time the boundary shows one of those outcomes — not `trackOnce`,
 * because how often a user hits them across a session is the point. Boot stays
 * on the idle path: anyone who can reach this already fired `geocomply_eligible`,
 * which boots the SDK immediately.
 */
export function trackGeoComplyBoundaryShown(outcome: GeoComplyBoundaryOutcome): void {
    if (typeof window === 'undefined' || !config.logRocketId) return;
    if (!sessionActive) return;

    const epoch = sessionEpoch;
    const userId = lastUserId;
    withSdk((LogRocket) => {
        // A deferred load can drain this after a logout/account switch; don't
        // attribute the previous user's block to whoever is signed in now.
        if (!sessionOpStillValid(epoch, userId)) return;
        LogRocket.track('geocomply_boundary_shown', { outcome });
    });
}

function trackOnce(event: LogrocketEvent): void {
    if (!sessionActive) return;
    if (trackedEvents.has(event)) return;
    trackedEvents.add(event);
    const epoch = sessionEpoch;
    const userId = lastUserId;
    // Conditional-recording events boot the SDK immediately rather than waiting
    // for browser idle; otherwise LogRocket cannot observe the event that tells it
    // to retain the session. `is_employee` is not time-sensitive and can ride the
    // idle-deferred path.
    const bootImmediately = event === 'first_deposit' || event === 'geocomply_eligible';
    withSdk((LogRocket) => {
        // Like the queued `identify`, a deferred load can drain this after a
        // logout/account switch; don't replay the prior user's event.
        if (!sessionOpStillValid(epoch, userId)) return;
        LogRocket.track(event);
    }, bootImmediately);
}
